import { Button, Modal } from '@hydrodigital/design-system';
import { Form, notification, Divider, Spin, Tabs, Select, Button as AntButton } from 'antd';
import {  DeleteFilled } from '@ant-design/icons';
import React, { FC, useEffect, useState, useRef } from 'react';
import SelectLocation from '@/basicsComponents/Selects/SelectLocation';
import SelectInpFile from '@/basicsComponents/Selects/SelectInpFile';
import SelectAsset from '@/basicsComponents/Selects/SelectAsset';
import { KpiType, ControlKpiResponse } from '@/domain/ModelFile/controlkpi';
import InpService from '@/domain/ModelFile/InpService';
import useInpFiles from '@/hooks/useInpFiles';
import { m } from '@/paraglide/messages';
import { useLocale } from '@/contexts/LocaleContext';

const inpService = new InpService();

interface ControlKPIModalProps {
	isOpen: boolean;
	onClose: () => void;
	userId: string;
	organizationId: number;
	onSuccess?: () => void;
	mode?: 'create' | 'edit';
	inpId?: number;
	locationId?: number;
	locationName?: string;
	inpName?: string;
}

interface FormValues {
	locationId: number;
	inpId: number;
	objectiveFunction: KpiType;
	kpi1: KpiType;
	kpi2: KpiType;
	kpi3: KpiType;
}

type AssetSelection = {
	objectiveFunction: string[];
	kpi1: string[];
	kpi2: string[];
	kpi3: string[];
};

const ControlKPIModal: FC<ControlKPIModalProps> = ({
	isOpen,
	onClose,
	userId,
	organizationId,
	onSuccess,
	mode = 'create',
	inpId,
	locationId,
	locationName,
	inpName,
}) => {
	useLocale();
	const KPI_OPTIONS: { label: string; value: KpiType }[] = [
		{ label: m.kpi_overflow_volume(), value: 'disch_vol' },
		{ label: 'Flooding volume', value: 'flood_vol' },
		{ label: m.kpi_overflow_locations(), value: 'disch_loc' },
		{ label: 'Number of flooding locations', value: 'flood_num' },
	];
	const [form] = Form.useForm<FormValues>();

	const [selectedLocation, setSelectedLocation] = useState<number | null>(null);
	const [saving, setSaving] = useState(false);
	const [selectedAssets, setSelectedAssets] = useState<AssetSelection>({
		objectiveFunction: [],
		kpi1: [],
		kpi2: [],
		kpi3: [],
	});
	const [currentInpId, setCurrentInpId] = useState<number | null>(null);
	const [selectedAssetIds, setSelectedAssetIds] = useState<
		Record<keyof AssetSelection, string | null>
	>({
		objectiveFunction: null,
		kpi1: null,
		kpi2: null,
		kpi3: null,
	});
	const [existingControlKpi, setExistingControlKpi] = useState<ControlKpiResponse | null>(null);
	const [loadingControlKpi, setLoadingControlKpi] = useState(false);
	const [inpFilesLoaded, setInpFilesLoaded] = useState(false);


	const { inpFiles, setParams: setInpParams } = useInpFiles({
		organizationId,
		locationId: selectedLocation,
		size: 1000,
		requireLocation: true,
		onlyCompleted: true,
		hasControlKpi: false,
		name: null,
	});
	const selectLocationRef = useRef<{ setValue: (value: any) => void }>(null);
	const selectInpFileRef = useRef<{ setValue: (value: any) => void }>(null);
	const selectAssetRefs = {
		objectiveFunction: useRef<{ setValue: (value: string) => void }>(null),
		kpi1: useRef<{ setValue: (value: string) => void }>(null),
		kpi2: useRef<{ setValue: (value: string) => void }>(null),
		kpi3: useRef<{ setValue: (value: string) => void }>(null),
	};

	const objectiveFunction = Form.useWatch('objectiveFunction', form);
	const kpi1 = Form.useWatch('kpi1', form);
	const kpi2 = Form.useWatch('kpi2', form);
	const kpi3 = Form.useWatch('kpi3', form);

	const loadControlKpi = React.useCallback(async (inpId: number) => {
		setLoadingControlKpi(true);
		try {
			const controlKpiData = await inpService.getControlKpi(inpId);
			if (controlKpiData && controlKpiData.length > 0) {
				const existingData = controlKpiData[0];
				setExistingControlKpi(existingData);
				
			
				form.setFieldsValue({
					locationId: existingData.locationId,
					inpId: existingData.inpId,
					objectiveFunction: existingData.configuration.objective_function.type,
					kpi1: existingData.configuration.kpis[0]?.type || undefined,
					kpi2: existingData.configuration.kpis[1]?.type || undefined,
					kpi3: existingData.configuration.kpis[2]?.type || undefined,
				});

				setSelectedAssets({
					objectiveFunction: existingData.configuration.objective_function.assets || [],
					kpi1: existingData.configuration.kpis[0]?.assets || [],
					kpi2: existingData.configuration.kpis[1]?.assets || [],
					kpi3: existingData.configuration.kpis[2]?.assets || [],
				});

			
				setSelectedLocation(existingData.locationId);
				setCurrentInpId(existingData.inpId);
				
		
				if (mode !== 'edit') {
					selectLocationRef.current?.setValue(existingData.locationId);
				
				}
			} else {
				setExistingControlKpi(null);
			}
		} catch (error) {
			console.error('Error loading Recommendation Logic:', error);
		
			setExistingControlKpi(null);
		} finally {
			setLoadingControlKpi(false);
		}
	}, [form]);



	useEffect(() => {
		if (!isOpen) {
			form.resetFields();
			setSelectedLocation(null);
			setSelectedAssets({
				objectiveFunction: [],
				kpi1: [],
				kpi2: [],
				kpi3: [],
			});
			setSelectedAssetIds({
				objectiveFunction: null,
				kpi1: null,
				kpi2: null,
				kpi3: null,
			});
			setCurrentInpId(null);
			setExistingControlKpi(null);
			if (mode !== 'edit') {
				setInpFilesLoaded(false);
			}
		}
	}, [isOpen, form]);

	
	useEffect(() => {
		if (currentInpId && isOpen) {
			void loadControlKpi(currentInpId);
		}
	}, [currentInpId, isOpen, loadControlKpi]);


	useEffect(() => {
		if (isOpen && mode === 'edit' && inpId && locationId) {
			setSelectedLocation(locationId);
			setCurrentInpId(inpId);
			form.setFieldsValue({
				locationId: locationId,
				inpId: inpId,
			});
			
			
			selectLocationRef.current?.setValue(locationId);
		
		}
	}, [isOpen, mode, inpId, locationId, form]);


	useEffect(() => {
		if (mode !== 'edit' && selectedLocation) {
			setInpFilesLoaded(false);
			setInpParams((prev: any) => ({
				...prev,
				locationId: selectedLocation,
			}));
		}
	}, [selectedLocation, setInpParams, mode]);

	
	useEffect(() => {
		if (mode !== 'edit' && selectedLocation && inpFiles.length > 0) {
			setInpFilesLoaded(true);
			
		
			if (currentInpId) {
				const inpFile = inpFiles.find((file: any) => file.inpId === currentInpId);
				if (inpFile) {
					selectInpFileRef.current?.setValue(currentInpId);
				}
			}
		}
	}, [selectedLocation, inpFiles, currentInpId, mode]);

	const handleLocationSelect = (locationId: string | number) => {
		const numericLocationId = Number(locationId);
		form.setFieldValue('locationId', numericLocationId);
		setSelectedLocation(numericLocationId);

		form.setFieldValue('inpId', undefined);
		form.setFieldValue('objectiveFunction', undefined);
		form.setFieldValue('kpi1', undefined);
		form.setFieldValue('kpi2', undefined);
		form.setFieldValue('kpi3', undefined);

		setSelectedAssets({
			objectiveFunction: [],
			kpi1: [],
			kpi2: [],
			kpi3: [],
		});
		setSelectedAssetIds({
			objectiveFunction: null,
			kpi1: null,
			kpi2: null,
			kpi3: null,
		});
		setCurrentInpId(null);
		setExistingControlKpi(null);
		setInpFilesLoaded(false);
	};

	const handleAssetSelection = (val: any, kpiType: keyof AssetSelection) => {
		setSelectedAssetIds((prev) => ({
			...prev,
			[kpiType]: val,
		}));
	};

	const handleAddAsset = (kpiType: keyof AssetSelection) => {
		const assetId = selectedAssetIds[kpiType];
		if (assetId && !selectedAssets[kpiType].includes(assetId)) {
			setSelectedAssets((prev) => ({
				...prev,
				[kpiType]: [...prev[kpiType], assetId],
			}));
			setSelectedAssetIds((prev) => ({
				...prev,
				[kpiType]: null,
			}));
			selectAssetRefs[kpiType].current?.setValue('');

			form.validateFields([`${kpiType}Assets`]);
		}
	};

	const handleRemoveAsset = (kpiType: keyof AssetSelection, assetId: string) => {
		setSelectedAssets((prev) => ({
			...prev,
			[kpiType]: prev[kpiType].filter((id) => id !== assetId),
		}));

		form.validateFields([`${kpiType}Assets`]);
	};


	const requiresAssets = (kpiType: KpiType | undefined): boolean => {
		if (!kpiType) return false;
		return !['flood_vol', 'flood_num'].includes(kpiType);
	};

	const getAssetType = (kpiType: KpiType | undefined): string | null => {
		if (!kpiType) return null;
		if (['disch_vol', 'disch_loc'].includes(kpiType)) return 'OUTFALL';
		return null;
	};

	const renderAssetSection = (
		kpiType: keyof AssetSelection,
		selectedKpiValue: KpiType | undefined,
	) => {

		if (!selectedKpiValue || !requiresAssets(selectedKpiValue)) {
			return null;
		}

		return (
				<Form.Item
					label="Assets to include in Calculation"
					name={`${kpiType}Assets`}
					className="mt-4"
					required={!!selectedKpiValue}
				rules={
					selectedKpiValue
						? [
								{
									required: true,
									validator: () => {
										if (selectedAssets[kpiType].length === 0) {
											return Promise.reject(new Error('Please add at least one asset'));
										}
										return Promise.resolve();
									},
								},
							]
						: undefined
				}
			>
				<div>
					<div className="flex gap-2 items-center mb-3">
						<div className="flex-1">
							<SelectAsset
								ref={selectAssetRefs[kpiType]}
								inpId={currentInpId || 0}
								typeId={1}
								defaultValue={undefined}
								excludeAssets={selectedAssets[kpiType]}
								assetType={getAssetType(selectedKpiValue)}
								onSelect={(val: any) => {
									handleAssetSelection(val, kpiType);
								}}
							/>
						</div>
						<Button
							label="Add asset"
							variant="primary"
							size="small"
							iconName="plus-circle"
							onClick={() => handleAddAsset(kpiType)}
							disabled={!selectedLocation || !currentInpId || !selectedAssetIds[kpiType]}
						/>
					</div>

					{selectedAssets[kpiType].length > 0 && (
						<div className="flex flex-wrap gap-2 border border-gray-300 rounded bg-white p-2">
							{selectedAssets[kpiType].map((assetId) => (
								<div
									key={assetId}
									className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-sky-100"
								>
									<span className="text-slate-800 text-xs font-bold">{assetId}</span>
									<AntButton
										type="text"
										size="small"
										icon={<DeleteFilled />}
										onClick={() => handleRemoveAsset(kpiType, assetId)}
										className="!text-red-500 !min-w-0 !p-1"
									/>
								</div>
							))}
						</div>
					)}
				</div>
			</Form.Item>
		);
	};

	const handleSaveControlKPI = async (values: FormValues) => {
		setSaving(true);
		try {
			// Helper to get assets - return empty array for flood_vol and flood_num
		const getAssetsForKpi = (kpiType: KpiType | undefined | null, assets: string[]): string[] => {
			if (!kpiType || ['flood_vol', 'flood_num'].includes(kpiType)) return [];
			return assets;
		};

		const configuration = {
				objective_function: {
					type: values.objectiveFunction,
					assets: getAssetsForKpi(values.objectiveFunction, selectedAssets.objectiveFunction)
				},
				kpis: [
					{
						type: values.kpi1 || null,
						assets: getAssetsForKpi(values.kpi1, selectedAssets.kpi1)
					},
					{
						type: values.kpi2 || null,
						assets: getAssetsForKpi(values.kpi2, selectedAssets.kpi2)
					},
					{
						type: values.kpi3 || null,
						assets: getAssetsForKpi(values.kpi3, selectedAssets.kpi3)
					}
				]
			};

			console.log('Control KPI Configuration to save:', configuration);

			const response = await inpService.saveControlKpi({
				inpId: values.inpId,
				locationId: values.locationId,
				configuration
			});

			if (response.success) {
				notification.success({
					message: 'Successful',
					description: response.message || 'Recommendation Logic saved successfully',
				});

				onClose();
				onSuccess?.();
			} else {
				notification.error({
					message: 'Error',
					description: response.message || 'Failed to save Recommendation Logic',
				});
			}
		} catch (error) {
			console.error('Error saving Recommendation Logic:', error);
			notification.error({
				message: 'Error',
				description: 'Failed to save Recommendation Logic',
			});
		} finally {
			setSaving(false);
		}
	};

	const handleCancel = () => {
		if (!saving) {
			onClose();
		}
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleCancel}
			size="m"
			closeOnOverlayClick={false}
			closeOnEscape={!saving}
			showCloseButton={!saving}
			testId="add-control-kpi-modal"
		>
			<Modal.Header>
				<Modal.Title text={mode === 'edit' ? 'Edit Recommendation Logic' : 'Add Recommendation Logic'} />
			</Modal.Header>

			<Modal.Content>
				<div className="px-4">
					<Divider className="!border-gray-200 !my-4" />

						<Form
							layout="vertical"
							form={form}
							onFinish={(values: FormValues) => {
								void handleSaveControlKPI(values);
							}}
							className="max-w-xl"
							disabled={saving}
						>
							<Form.Item
								label={'Select location'}
								name="locationId"
								rules={[
									{
										required: true,
										message: 'Please select a location',
									},
								]}
							>
							{mode === 'edit' ? (
								<div className="py-1.5 px-3 border border-gray-300 rounded-md bg-gray-100">
									{locationName || 'Location'}
								</div>
							) : (
								<SelectLocation
									ref={selectLocationRef}
									userId={userId}
									onSelect={handleLocationSelect}
								/>
							)}
							</Form.Item>

							<Form.Item
								label={'Model INP'}
								name="inpId"
								rules={[
									{ required: true, message: 'Please select an INP file' },
									{
										validator: (_, value) =>
											value !== 0
												? Promise.resolve()
												: Promise.reject(new Error('Please select an INP file')),
									},
								]}
							>
							{mode === 'edit' ? (
								<div className="py-1.5 px-3 border border-gray-300 rounded-md bg-gray-100">
									{inpName || 'INP file not found'}
								</div>
								) : selectedLocation ? (
									<SelectInpFile
										ref={selectInpFileRef}
										onSelect={(val: string | number) => {
											const inpId = Number(val);
											form.setFieldValue('inpId', inpId);
											setCurrentInpId(inpId);
										}}
										organizationId={organizationId}
										locationId={selectedLocation}
										requireLocation
										onlyCompleted
										hasControlKpi={false}
									/>
								) : (
									<div className="py-1.5 px-3 border border-gray-300 rounded-md bg-gray-100 text-gray-400 cursor-not-allowed">
										Please select a location first
									</div>
								)}
							</Form.Item>

							<div className="mt-2">
							<Tabs
								defaultActiveKey="1"
								type="line"
								size="small"
								className={`mb-4 border-b border-gray-100 ${!selectedLocation ? 'opacity-50 pointer-events-none' : ''}`}
									items={[
										{
											key: '1',
										label: (
											<span className="text-slate-800">
												<span className="inline-block mr-1 text-red-500 text-sm font-mono leading-none">
													*
												</span>
												<span className="text-slate-800 font-bold">
													{m.optimization_target()}
												</span>
											</span>
											),
											children: (
												<>
													<Form.Item
														name="objectiveFunction"
														rules={[
															{
																required: true,
																message: m.optimization_target_required(),
															},
														]}
													>
														<Select
															placeholder={
																selectedLocation
																	? m.optimization_target_placeholder()
																	: 'Please select a location first'
															}
															options={KPI_OPTIONS}
															disabled={!selectedLocation}
															allowClear
															onChange={(value) => {
																if (!value) {
																	setSelectedAssets((prev) => ({
																		...prev,
																		objectiveFunction: [],
																	}));
																	setSelectedAssetIds((prev) => ({
																		...prev,
																		objectiveFunction: null,
																	}));
																	selectAssetRefs.objectiveFunction.current?.setValue('');
																}
															}}
														/>
													</Form.Item>
													{renderAssetSection('objectiveFunction', objectiveFunction)}
												</>
											),
										},
										{
											key: '2',
											label: <span className="text-slate-800 font-bold">Safety #1</span>,
											children: (
												<>
													<Form.Item name="kpi1">
														<Select
															placeholder={
																selectedLocation
																	? 'Select Safaty Priority #1'
																	: 'Please select a location first'
															}
															options={KPI_OPTIONS}
															disabled={!selectedLocation}
															allowClear
															onChange={(value) => {
																if (!value) {
																	setSelectedAssets((prev) => ({
																		...prev,
																		kpi1: [],
																	}));
																	setSelectedAssetIds((prev) => ({
																		...prev,
																		kpi1: null,
																	}));
																	selectAssetRefs.kpi1.current?.setValue('');
																}
															}}
														/>
													</Form.Item>
													{renderAssetSection('kpi1', kpi1)}
												</>
											),
										},
										{
											key: '3',
											label: <span className="text-slate-800 font-bold">Safety #2</span>,
											children: (
												<>
													<Form.Item name="kpi2">
														<Select
															placeholder={
																selectedLocation
																	? 'Select Safety Priority #2'
																	: 'Please select a location first'
															}
															options={KPI_OPTIONS}
															disabled={!selectedLocation}
															allowClear
															onChange={(value) => {
																if (!value) {
																	setSelectedAssets((prev) => ({
																		...prev,
																		kpi2: [],
																	}));
																	setSelectedAssetIds((prev) => ({
																		...prev,
																		kpi2: null,
																	}));
																	selectAssetRefs.kpi2.current?.setValue('');
																}
															}}
														/>
													</Form.Item>
													{renderAssetSection('kpi2', kpi2)}
												</>
											),
										},
										{
											key: '4',
											label: <span className="text-slate-800 font-bold">Safety #3</span>,
											children: (
												<>
													<Form.Item name="kpi3">
														<Select
															placeholder={
																selectedLocation
																	? 'Select Safety Priority #3'
																	: 'Please select a location first'
															}
															options={KPI_OPTIONS}
															disabled={!selectedLocation}
															allowClear
															onChange={(value) => {
																if (!value) {
																	setSelectedAssets((prev) => ({
																		...prev,
																		kpi3: [],
																	}));
																	setSelectedAssetIds((prev) => ({
																		...prev,
																		kpi3: null,
																	}));
																	selectAssetRefs.kpi3.current?.setValue('');
																}
															}}
														/>
													</Form.Item>
													{renderAssetSection('kpi3', kpi3)}
												</>
											),
										},
									]}
								/>
							</div>
						</Form>
				</div>
			</Modal.Content>

			<Modal.Footer className='flex justify-between px-5 py-2'>
				<Button
					label={saving ? (mode === 'edit' ? 'Updating...' : 'Adding...') : (mode === 'edit' ? 'Update' : 'Add')}
					variant="primary"
					showIcon={false}
					onClick={() => form.submit()}
					disabled={saving || loadingControlKpi}
					loading={saving}
				/>

				<Button label="Cancel" showIcon={false} variant="secondary" onClick={handleCancel} disabled={saving} />
			</Modal.Footer>
		</Modal>
	);
};

export default ControlKPIModal;
